import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '@shared/components/header/header.component';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TableComponent } from '@shared/components/table/table.component';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CardComponent } from '@shared/components/card/card.component';
import { connectionpointTable } from '@shared/table-configs/connectionpoint/connectionpoint.table';
import { LucideAngularModule } from 'lucide-angular';
import {
  connectionpointSearchActionMenu
} from '@views/connectionpoint/connectionpoint-search/connectionpoint-search.action-menu';
import {
  CloseConnectionpointComponent
} from '@shared/modals/connectionpoint/close-connectionpoint/close-connectionpoint.component';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionPointSearchQuery, ConnectionPointSearchResult, ConnectionPointService } from '@app/api/nts';
import { Observable } from 'rxjs';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { copyToClipboard } from '@app/core/utils/copy-to-clipboard';
import { ToastService } from '@app/core/services/toast.service';
import { SortParam } from '@app/core/constants/sort-param';
import { hasRole } from '@app/core/guards/has-role.guard';
import { AuthRoles } from '@app/api/dmc';

@Component({
  selector: 'app-connectionpoint-search',
  imports: [
    ButtonDirective,
    HeaderComponent,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    LucideAngularModule,
    ReactiveFormsModule,
    TableComponent,
    TranslatePipe,
    CardComponent,
  ],
  templateUrl: './connectionpoint-search.component.html',
  styleUrl: './connectionpoint-search.component.css'
})
export class ConnectionpointSearchComponent implements AfterViewInit {
  @ViewChild('table', {static: false}) public table?: TableComponent<any>;

  public cpWriter = hasRole([AuthRoles.ConnectionPointWriter]);

  private readonly actions: MenuAction[] = [
    {
      ...connectionpointSearchActionMenu[0],
      callback: () => this.openCreateModal(),
      disabled: () => !this.cpWriter
    },
    {
      ...connectionpointSearchActionMenu[1],
      callback: () => this.createRelatedMeteringPoint(),
      disabled: () => !this.cpWriter || this.selectedElements.length !== 1 || this.selectedElements[0].meteringPointIdentification !== '',
      breaker: true,
    },
    {
      ...connectionpointSearchActionMenu[2],
      callback: () => this.openShutdownModal(),
      disabled: () => !this.cpWriter || this.selectedElements.length !== 1
    },
  ]

  public readonly connectionPointTableConfig: TableConfig<ConnectionPointSearchResult> = {
    ...connectionpointTable,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.search(pageSize, page, order),
    contextActions: [
      this.actions[1],
      this.actions[2],
      {
        id: 'copy',
        title: 'common.copy',
        breaker: true,
        callback: () => this.copyToClipBoard(),
      }
    ]
  };

  public queryForm = new FormGroup({
    connectionPointIdentification: new FormControl<string | null>(null),
    alternativeConnectionPointIdentification: new FormControl<string | null>(null),
    streetName: new FormControl<string | null>(null),
    streetNumber: new FormControl<string | null>(null),
    floorIdentification: new FormControl<string | null>(null),
    postalCode: new FormControl<string | null>(null),
    meterNumber: new FormControl<string | null>(null),
    stationNumber: new FormControl<string | null>(null),
    meteringPointIdentification: new FormControl<string | null>(null),
  });

  public selectedElements: ConnectionPointSearchQuery[] = [];

  constructor(
    private readonly connectionPointService: ConnectionPointService,
    private readonly modalService: ModalService,
    private readonly headerMenuService: HeaderService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastService: ToastService,
  ) {
    this.headerMenuService.setActions(this.actions);
  }

  public ngAfterViewInit(): void {
    this.route.queryParams.subscribe(() => {
      this.populateFormFromParams();
    });
  }

  private populateFormFromParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    if (Object.keys(queryParams).length > 0) {
      this.queryForm.patchValue(queryParams);
      this.queryForm.markAsDirty();
      this.table?.search();
    }
  }

  /**
   * Search for a connection point
   * @param pageSize
   * @param page
   * @param order
   */
  public search(pageSize: number, page: number, order?: SortParam): Observable<any> {
    const sort = order ? {SortParams: [order]} : {};
    const query = removeNullKeys(this.queryForm.value);
    return this.connectionPointService.connectionPointSearchPost({...query}, page, pageSize);
  }

  private copyToClipBoard(): void {
    if (this.selectedElements.length !== 1) return;

    copyToClipboard(this.selectedElements[0].connectionPointIdentification!).then(() => {
      this.toastService.show('connectionPoint.action.copy.success');
    });
  }

  public createRelatedMeteringPoint(): void {
    this.router.navigate(['/meteringpoint', 'create'], {
      queryParams: {
        connectionPointIdentification: this.selectedElements[0].connectionPointIdentification,
        meteringPointType: 'Consumption'
      }
    });
  }

  public openCreateModal(): void {
    this.router.navigate(['/connectionpoint', 'create']);
  }

  public performSearch(): void {
    this.table?.search();
    this.updateUriWithParams();
  }

  private updateUriWithParams(): void {
    const rawValues = this.queryForm.getRawValue();
    const queryParams: { [key: string]: string | null } = {};

    for (const [key, value] of Object.entries(rawValues)) {
      queryParams[key] = (value === null || value === undefined || value === '') ? null : value as string;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }

  public reset(): void {
    this.queryForm.reset();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  public openShutdownModal(): void {
    if (this.selectedElements.length !== 1) return;

    const id: string | undefined = this.selectedElements[0].connectionPointIdentification!;

    this.modalService.show({
      component: CloseConnectionpointComponent,
      title: this.translateService.instant('action.close') + ' ' +
        this.translateService.instant('connectionpoint.title'),
      actions: [
        {
          type: 'primary',
          label: 'system.button.disband',
          method: 'submit'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id }
    }).subscribe(() => {
      this.table?.search();
    })
  }

}
